import React, { useCallback, useEffect, useState } from "react";
import {
  InputAdornment,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Box,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { Button } from "rsuite";
import { TextFieldComponent, TextFieldSelect } from "../../components";
import { toast } from "react-toastify";
import { merchantApi } from "../../redux/api/apiConfig";
import {
  INTERNAL_TRANSFER,
  ACCOUNTS_LIST,
  CALCULATE_REMITTANCE_FEES,
} from "../../ApiUrl";
import { handleApiErrors } from "../../utils/common";
import { formatCurrency } from "../../utils/common";
import PaymentDetails from "../../pages/outgoingTransfer/components/PaymentDetails";
import * as API from "../../ApiUrl";

const validationSchema = yup.object({
  creditor: yup.string().required("To account is required."),
  debtor: yup.string().required("From account is required."),
  // description: yup.string().required("Description is required"),
  amount: yup
    .number("Enter amount")
    .min(0, "Please enter valid amount")
    .required("Amount is required!"),
});

const InternalTransferModal = ({ onClose, isOpen, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [loadingFees, setLoadingFees] = useState(false);
  const [fxSpotId, setFxSpotId] = useState(null);

  // Fetch accounts using the same API as outgoing transfer
  const fetchAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      const response = await merchantApi.get(ACCOUNTS_LIST, {
        params: {
          underlying: true,
          includeInactiveAccounts: true,
        },
      });
      
      const accountsData = response.data?.data?.content || [];
      setAccounts(accountsData);
      setLoadingAccounts(false);
    } catch (error) {
      setLoadingAccounts(false);
      toast.error("Error loading accounts");
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchAccounts();
    }
  }, [isOpen, fetchAccounts]);

  const addClickHandler = async (values) => {
    console.log("internal transfer values", values);

    try {
      setLoading(true);
      
      // Prepare payload with new structure
      const payload = {
        senderAccountId: values?.debtor || "", // From account ID
        receiverAccountId: values?.creditor || "", // To account ID
        amount: values?.amount || 0,
        remarks: values?.description || "", // Description field
      };

      // Add fxSpotId if available from fees API response
      if (fxSpotId) {
        payload.fxSpotId = fxSpotId;
      }

      const response = await merchantApi.post("/admin_panel/api/a2a-transaction/", payload);
      setLoading(false);
      toast.success("Internal Transfer Created Successfully!");
      onSubmit();
    } catch (error) {
      setLoading(false);
      // Handle errors
      const errorMessages = handleApiErrors(error);
      // Display each error in a separate snackbar
      errorMessages.forEach(({ field, message }) => {
        const formattedMessage = field ? `${field}: ${message}` : message; // Include field name if available
        toast.error(formattedMessage);
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      debtor: "",
      creditor: "",
      amount: "",
      description: "",
    },
    validateOnBlur: true,
    validationSchema: validationSchema,
    validateOnMount: true,
    onSubmit: addClickHandler,
  });

  // Get selected account objects
  const getDebtorAccount = () => {
    return accounts.find(account => account.accountId === formik.values.debtor);
  };

  const getCreditorAccount = () => {
    return accounts.find(account => account.accountId === formik.values.creditor);
  };

  // Get currencies from selected accounts
  const getSenderCurrency = () => {
    const debtorAccount = getDebtorAccount();
    return debtorAccount?.currencyCode || "USD";
  };

  const getRecipientCurrency = () => {
    const creditorAccount = getCreditorAccount();
    return creditorAccount?.currencyCode || "USD";
  };

  // Calculate fees using API when amount changes
  const calculateFees = useCallback(
    async (sendAmount, debtorAccountId, creditorAccountId) => {
      if (!sendAmount || sendAmount <= 0) {
        setFeeData(null);
        return;
      }

      const debtorAccount = accounts.find(account => account.accountId === debtorAccountId);
      const creditorAccount = accounts.find(account => account.accountId === creditorAccountId);

      const senderCurrency = debtorAccount?.currencyCode || "USD";
      const recipientCurrency = creditorAccount?.currencyCode || "USD";

      if (!senderCurrency || !recipientCurrency || !debtorAccountId || !creditorAccountId) {
        return;
      }

      try {
        setLoadingFees(true);
        
        const response = await merchantApi.post(API.CALCULATE_REMITTANCE_FEES, {
          send_amount: sendAmount,
          recipient_currency: recipientCurrency,
          sender_currency: senderCurrency,
        });
        
        const feeResponseData = response.data?.data;
        
        if (feeResponseData) {
          const fees = {
            send_amount: feeResponseData.send_amount,
            transaction_fee: feeResponseData.transaction_fee || 0,
            total_fee: feeResponseData.total_fee || 0,
            user_pay: feeResponseData.user_pay,
            recipient_amount: feeResponseData.recipient_amount || feeResponseData.receive_amount,
            exchange_rate: feeResponseData.exchange_rate,
          };
          
          setFeeData(fees);
          
          // Extract fxSpotId from buckzy_data
          const spotId = feeResponseData.buckzy_data?.fxSpotId || null;
          setFxSpotId(spotId);
        } else {
          setFeeData(null);
          setFxSpotId(null);
          toast.error("Invalid response from fee calculation API");
        }
        
        setLoadingFees(false);
      } catch (error) {
        setLoadingFees(false);
        const errorMessage = 
          error?.response?.data?.message || 
          error?.response?.data?.error ||
          error?.message || 
          "Error calculating fees";
        toast.error(errorMessage);
        setFeeData(null);
        setFxSpotId(null);
      }
    },
    [accounts]
  );

  // Debounced amount change - recalculate fees when amount or accounts change
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (formik.values.amount > 0 && formik.values.debtor && formik.values.creditor) {
        calculateFees(formik.values.amount, formik.values.debtor, formik.values.creditor);
      } else {
        setFeeData(null);
        setFxSpotId(null);
      }
    }, 500);

    return () => clearTimeout(timerId);
  }, [formik.values.amount, formik.values.debtor, formik.values.creditor, calculateFees]);

  // Get filtered accounts for "To" field (exclude selected "From" account)
  const getFilteredToAccounts = () => {
    if (!formik.values.debtor) {
      return accounts;
    }
    return accounts.filter(account => account.accountId !== formik.values.debtor);
  };

  // Clear "To" field when "From" account changes
  useEffect(() => {
    if (formik.values.debtor) {
      // If the currently selected "To" account is the same as "From", clear it
      if (formik.values.creditor === formik.values.debtor) {
        formik.setFieldValue("creditor", "");
      }
      // Clear fee data when accounts change
      setFeeData(null);
      setFxSpotId(null);
    }
  }, [formik.values.debtor]);

  // Clear fee data when creditor changes
  useEffect(() => {
    if (formik.values.creditor) {
      setFeeData(null);
      setFxSpotId(null);
    }
  }, [formik.values.creditor]);

  const handleCloseModal = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    onClose();
  };

  return (
    <>
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth={"lg"}
        onClose={handleCloseModal}
        scroll={"body"}
        BackdropProps={{ style: { backgroundColor: "rgba(11, 5, 35, 0.8)" } }}
        PaperProps={{
          style: {
            boxShadow: "none",
            borderRadius: "8px",
            minHeight: "500px",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          Create Internal Transfer
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent
            sx={{ paddingTop: "5px !important", paddingBottom: "0 !important" }}
          >
            <Grid container spacing={3}>
              {/* Left Column - Form Fields */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <TextFieldSelect
                    formik={formik}
                    name="debtor"
                    label="From"
                    options={accounts}
                    render={(account) => {
                      const balance = account.balance !== undefined ? account.balance : 0;
                      const currency = account.currencyCode || "USD";
                      const formattedBalance = formatCurrency(balance, 2, "", false);
                      const accountName = account.financialInstitutionDetails?.accountName || "";
                      return (
                        <MenuItem key={account.accountId} value={account.accountId}>
                          <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
                            <Stack direction="column" spacing={0.5}>
                              <Typography variant="body2">
                                {account.nickName} ({account.accountId})
                              </Typography>
                              {accountName && (
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {accountName}
                                </Typography>
                              )}
                            </Stack>
                            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
                              {currency} {formattedBalance}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      );
                    }}
                    required
                    disabled={loadingAccounts}
                  />
                  <TextFieldSelect
                    formik={formik}
                    name="creditor"
                    label="To"
                    options={getFilteredToAccounts()}
                    render={(account) => {
                      const balance = account.balance !== undefined ? account.balance : 0;
                      const currency = account.currencyCode || "USD";
                      const formattedBalance = formatCurrency(balance, 2, "", false);
                      const accountName = account.financialInstitutionDetails?.accountName || "";
                      return (
                        <MenuItem key={account.accountId} value={account.accountId}>
                          <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
                            <Stack direction="column" spacing={0.5}>
                              <Typography variant="body2">
                                {account.nickName} ({account.accountId})
                              </Typography>
                              {accountName && (
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {accountName}
                                </Typography>
                              )}
                            </Stack>
                            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
                              {currency} {formattedBalance}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      );
                    }}
                    required
                    disabled={loadingAccounts || !formik.values.debtor}
                  />
                  <TextFieldComponent
                    formik={formik}
                    name={"amount"}
                    type={"number"}
                    label={"Amount"}
                    required
                    inputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getSenderCurrency()}
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      formik.setFieldValue("amount", value);
                    }}
                  />
                  <TextFieldComponent
                    formik={formik}
                    name={"description"}
                    type={"text"}
                    label={"Description"}
                    multiline
                    rows={4}
                  />
                </Stack>
              </Grid>

              {/* Right Column - Payment Details */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    height: "fit-content",
                    borderLeft: { md: "1px solid", xs: "none" },
                    borderColor: "divider",
                    pl: { md: 3, xs: 0 },
                    pt: { xs: 2, md: 0 },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    {loadingFees && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          zIndex: 1,
                          borderRadius: 1,
                        }}
                      >
                        <CircularProgress size={40} />
                      </Box>
                    )}
                    <PaymentDetails
                      fee={feeData || null}
                      senderCurrency={getSenderCurrency()}
                      recieverCurrency={getRecipientCurrency()}
                      balance={0}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <DialogActions sx={{ justifyContent: "space-between", mt: 2 }}>
              <Button
                className="btn white-btn action-button "
                onClick={onClose}
                appearance="subtle"
              >
                Cancel
              </Button>
              <Button
                className={`btn action-button primary_color text-white`}
                appearance="primary"
                type={"submit"}
                disabled={!formik.isValid}
                loading={loading}
              >
                Create
              </Button>
            </DialogActions>
          </DialogContent>
        </form>
      </Dialog>
    </>
  );
};

export default InternalTransferModal;
